import { PlaneView } from '../../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { ViewportCursor } from './ViewportCursor';

/**
 * Common interface for tool cursors that can be set, reset,
 * and stopped. Used by {@link RegistrationTool} to hold
 * either a {@link TranslationCursor} or {@link RotationCursor}
 * without coupling to concrete types.
 */
export interface ToolCursor {
    setCustomCursor(): void;
    resetCursor(): void;
    stopAction(): EventHandlerResult;
}

/**
 * Abstract base class for SVG-based CSS cursors that target
 * canvas parent elements. Encapsulates the shared logic of
 * encoding an SVG string to a base64 data URI, applying it
 * to all viewport parent elements (or document.body as
 * fallback), and resetting back to default.
 *
 * Subclasses provide the SVG content via {@link getSvgString}
 * and the CSS fallback cursor via {@link getFallbackCursor}.
 */
export abstract class SvgCursor extends ViewportCursor implements ToolCursor {
    constructor(views: PlaneView[] | null) {
        super(views);
        this.setCustomCursor();
    }

    /** Returns the SVG markup for the current cursor state. */
    protected abstract getSvgString(): string;

    /** Returns the CSS fallback cursor keyword
     *  (e.g. 'grab', 'move', 'alias'). */
    protected abstract getFallbackCursor(): string;

    /** Returns the cursor hotspot position.
     *  Override in subclasses with different SVG dimensions. */
    protected getHotspot(): { x: number; y: number } {
        return { x: 12, y: 12 };
    }

    public setCustomCursor(): void {
        const svg = this.getSvgString();
        const base64Cursor = `data:image/svg+xml;base64,${btoa(svg)}`;
        const fallback = this.getFallbackCursor();
        const hotspot = this.getHotspot();
        this.setParentsCursorStyle(
            `url(${base64Cursor}) ${hotspot.x} ${hotspot.y}, ${fallback}`
        );
    }

    public resetCursor(): void {
        this.resetParentsCursor();
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
